/**
 * Constructor.io Shared Templates
 * Modern template engine for product rendering across Constructor.io implementations
 */

class ConstructorTemplates {
  /**
   * Render a single product template
   * @param {object} product - Product data
   * @param {object} options - Rendering options
   * @returns {string} HTML string
   */
  static renderSingleProduct(product, options = {}) {
    const validation = ConstructorUtils.validateProduct(product);
    if (!validation.isValid) return "";

    const config = this.buildProductConfig(product, options);
    const imageData = this.buildImageData(product, config);
    const pricingData = this.buildPricingData(product, config);
    const attributes = this.buildAttributes(product, config);
    const badges = this.buildBadges(product, pricingData, config);
    const swatches = config.shouldRenderSwatches ? this.buildSwatches(product, config) : null;

    return this.generateProductHTML({
      product,
      config,
      imageData,
      pricingData,
      attributes,
      badges,
      swatches,
    });
  }

  /**
   * Build product configuration
   * @param {object} product - Product data
   * @param {object} options - User options
   * @returns {object} Product configuration
   */
  static buildProductConfig(product, options = {}) {
    const productIndex = options.productIndex !== undefined ? options.productIndex : 0;
    const isAboveTheFold = productIndex < 4; // Performance threshold

    const imgOrientation = ConstructorUtils.getThemeConfig("productCard.imgOrientation") || "square";
    const dimensions = ConstructorUtils.getImageDimensions(imgOrientation, {
      defaultWidth: options.imageWidth || 600,
      defaultHeight: options.imageHeight || 600,
    });

    return {
      productIndex,
      isAboveTheFold,
      shouldRenderSwatches: isAboveTheFold && ConstructorUtils.getThemeConfig("productCard.showVariantSwatches"),
      imageOrientation: imgOrientation,
      imageWidth: dimensions.width,
      imageHeight: dimensions.height,
      cardStyle: ConstructorUtils.getThemeConfig("productCard.style") || "",
      showWishlist: ConstructorUtils.getThemeConfig("productCard.showWishlistBtn"),
      showRating: ConstructorUtils.getThemeConfig("productCard.showRating"),
      showBadges: ConstructorUtils.getThemeConfig("productCard.showBadges"),
      context: options.context || "grid",
    };
  }

  /**
   * Build image data for product
   * @param {object} product - Product data
   * @param {object} config - Product configuration
   * @returns {object} Image data
   */
  static buildImageData(product, config) {
    const mediaImages = ConstructorUtils.processMediaImages(product);
    const constructorData = product.constructorData?.data || product.constructorData || {};

    const primarySrcRaw =
      product.featured_image?.src || mediaImages[0]?.transformedSrc || mediaImages[0]?.src || constructorData.image_url || "";

    const secondarySrcRaw = mediaImages[1]?.transformedSrc || mediaImages[1]?.src || null;

    const primarySrc = primarySrcRaw
      ? ConstructorUtils.buildSizedImageUrl(primarySrcRaw, config.imageWidth, config.imageHeight)
      : "";
    const secondarySrc = secondarySrcRaw
      ? ConstructorUtils.buildSizedImageUrl(secondarySrcRaw, config.imageWidth, config.imageHeight)
      : null;

    const isFirstProduct = config.productIndex === 0;

    // SEO fallback: genuine admin alt wins; otherwise "<title> - <type> | EVRYJEWELS"
    // (type segment omitted when blank).
    const productType = product.product_type || constructorData.product_type || "";

    return {
      primarySrc,
      secondarySrc,
      alt: ConstructorUtils.sanitizeHTML(
        mediaImages[0]?.altText ||
          `${product.title || ""}${productType ? " - " + productType : ""} | EVRYJEWELS`
      ).replace(/"/g, "&quot;"), // sanitizeHTML escapes &<> but not quotes; guard the attribute
      loading: config.isAboveTheFold ? "eager" : "lazy",
      fetchpriority: isFirstProduct ? "high" : "low",
    };
  }

  /**
   * Build pricing data for product
   * @param {object} product - Product data
   * @param {object} config - Product configuration
   * @returns {object} Pricing data
   */
  static buildPricingData(product, config) {
    const constructorData = product.constructorData?.data || product.constructorData || {};
    const firstVariant = product.variants?.[0] || {};

    let basePrice = firstVariant.price || constructorData.price || 0;
    let compareAtPrice = firstVariant.compare_at_price || constructorData.compareAtPrice || 0;

    // Apply fake discount if applicable
    const fakeDiscountResult = this.calculateFakeDiscount(product);
    if (fakeDiscountResult.shouldApply) {
      compareAtPrice = basePrice;
      basePrice = fakeDiscountResult.discountedPrice;
    }

    const isOnSale = compareAtPrice > basePrice;
    const discountPercentage = isOnSale ? Math.round(((compareAtPrice - basePrice) / compareAtPrice) * 100) : 0;

    return {
      price: basePrice,
      compareAtPrice,
      isOnSale,
      discountPercentage,
      formattedPrice: ConstructorUtils.formatMoney(basePrice),
      formattedComparePrice: isOnSale ? ConstructorUtils.formatMoney(compareAtPrice) : null,
      fakeDiscountData: fakeDiscountResult,
    };
  }

  /**
   * Calculate fake discount for product
   * @param {object} product - Product data
   * @returns {object} Fake discount result
   */
  static calculateFakeDiscount(product) {
    const fakeDiscountSettings = ConstructorUtils.getThemeConfig("fakeDiscount");
    if (!fakeDiscountSettings?.enabled) {
      return { shouldApply: false };
    }

    const constructorData = product.constructorData?.data || product.constructorData || {};
    const discountPercentage = constructorData.fake_discount_percentage;
    const discountMessage = constructorData.fake_discount_message;

    if (!discountPercentage || discountPercentage <= 0) {
      return { shouldApply: false };
    }

    const basePrice = product.variants?.[0]?.price || constructorData.price || 0;
    const discountedPrice = Math.round(basePrice * (1 - discountPercentage / 100));

    return {
      shouldApply: true,
      percentage: discountPercentage,
      message: discountMessage,
      discountedPrice,
      originalPrice: basePrice,
    };
  }

  /**
   * Build product attributes
   * @param {object} product - Product data
   * @param {object} config - Product configuration
   * @returns {object} Product attributes
   */
  static buildAttributes(product, config) {
    const constructorData = product.constructorData?.data || product.constructorData || {};

    return {
      title: product.title || "",
      handle: product.handle || "",
      url: product.url || constructorData.url || `/products/${product.handle}`,
      vendor: product.vendor || constructorData.vendor || "",
      productType: product.product_type || constructorData.product_type || "",
      available: product.available !== undefined ? product.available : true,
      tags: Array.isArray(product.tags)
        ? product.tags
        : constructorData.tags
        ? constructorData.tags.split(",").map((tag) => tag.trim())
        : [],
      id: product.id || constructorData.id || ConstructorUtils.generateId("product"),
    };
  }

  /**
   * Build badge data for product
   * @param {object} product - Product data
   * @param {object} pricingData - Pricing data
   * @param {object} config - Product configuration
   * @returns {object} Badge data
   */
  static buildBadges(product, pricingData, config) {
    if (!config.showBadges) {
      return { primary: null, sale: null };
    }

    const constructorData = product.constructorData?.data || product.constructorData || {};
    // Get tags from multiple possible sources
    const rawTags = product.tags || constructorData.tags || [];
    const tags = Array.isArray(rawTags) ? rawTags : typeof rawTags === "string" ? rawTags.split(",").map((t) => t.trim()) : [];

    // Check for preorder (highest priority)
    const isPreorder = tags.some((tag) => tag.toLowerCase().includes("preorder") || constructorData.preorder_tag);

    if (isPreorder) {
      return { primary: null, sale: null }; // No badges for preorder
    }

    // Primary badge hierarchy
    let primaryBadge = null;

    if (pricingData.fakeDiscountData.shouldApply && ConstructorUtils.getThemeConfig("customBadge.showPromoBadge")) {
      primaryBadge = {
        type: "promo",
        text: pricingData.fakeDiscountData.message || `${pricingData.fakeDiscountData.percentage}% OFF`,
        class: "custom-badge",
      };
    } else if (ConstructorUtils.getThemeConfig("customBadge.showNewCustomBadge")) {
      // Check for required tag filter
      const requiredTag = ConstructorUtils.getThemeConfig("customBadge.marketSettings.tag");
      const hasRequiredTag =
        !requiredTag || requiredTag.trim() === "" || tags.some((tag) => tag.toLowerCase() === requiredTag.toLowerCase().trim());

      if (hasRequiredTag) {
        primaryBadge = {
          type: "custom",
          text:
            ConstructorUtils.getThemeConfig("customBadge.marketSettings.text") ||
            ConstructorUtils.getThemeConfig("customBadge.newCustomBadgeText") ||
            "NEW",
          class: "new-custom-badge",
        };
      }
    }

    if (!primaryBadge) {
      // Commercial badge logic
      const commercialBadge = this.getCommercialBadge(tags, constructorData);
      if (commercialBadge) {
        primaryBadge = commercialBadge;
      }
    }

    // Sale badge
    // Skipped when the merchant moved the sale badge to the label bar above the card
    const saleBadge =
      pricingData.isOnSale &&
      ConstructorUtils.getThemeConfig("productCard.showSaleBadge") &&
      !ConstructorUtils.getThemeConfig("productCard.saleBadgeAboveCard")
        ? {
            type: "sale",
            text: `${pricingData.discountPercentage}% OFF`,
            class: `badge-${ConstructorUtils.getThemeConfig("productCard.saleBadgeColor") || "primary"}`,
          }
        : null;

    return { primary: primaryBadge, sale: saleBadge };
  }

  /**
   * Get commercial badge based on product tags
   * @param {Array} tags - Product tags
   * @param {object} constructorData - Constructor data
   * @returns {object|null} Commercial badge or null
   */
  static getCommercialBadge(tags, constructorData) {
    const commercialSettings = ConstructorUtils.getThemeConfig("commercialBadge");
    if (!commercialSettings?.showCommercialBadge) return null;

    // Priority order: waterproof > new > bestsellers > low-inventory
    if (constructorData.waterproof_tag && commercialSettings.showWaterproofBadge) {
      return {
        type: "waterproof",
        text: commercialSettings.waterproofBadgeText || "WATERPROOF",
        class: "waterproof-badge",
      };
    }

    if (constructorData.new_tag && commercialSettings.showNewBadge) {
      return {
        type: "new",
        text: commercialSettings.newBadgeText || "NEW",
        class: "new-badge",
      };
    }

    if (constructorData.bestseller_tag && commercialSettings.showBestsellersBadge) {
      return {
        type: "bestseller",
        text: commercialSettings.bestsellersBadgeText || "BESTSELLER",
        class: "bestsellers-badge",
      };
    }

    // Low inventory check
    const inventoryQuantity = constructorData.inventory_quantity;
    if (
      commercialSettings.showLowInventoryBadge &&
      inventoryQuantity !== undefined &&
      inventoryQuantity <= (commercialSettings.lowInventoryThreshold || 5)
    ) {
      return {
        type: "low-inventory",
        text: commercialSettings.lowInventoryBadgeText || "LOW STOCK",
        class: "low-inventory-badge",
      };
    }

    return null;
  }

  /**
   * Build variant swatches
   * @param {object} product - Product data
   * @param {object} config - Product configuration
   * @returns {object|null} Swatches data
   */
  static buildSwatches(product, config) {
    const swatchSettings = ConstructorUtils.getThemeConfig("variantSwatches");
    if (!swatchSettings?.enabled) return null;

    const variations = product.variations || [];
    if (!variations.length) return null;

    const colorVariations = variations.filter(
      (variation) => variation.name?.toLowerCase().includes("color") || variation.name?.toLowerCase().includes("colour")
    );

    if (!colorVariations.length) return null;

    const swatches = colorVariations.slice(0, swatchSettings.maxVisible || 4).map((variation) => ({
      name: variation.name,
      value: variation.value,
      normalizedValue: ConstructorUtils.normalizeColorName(variation.value),
      imageUrl: variation.imageUrl || null,
    }));

    return {
      swatches,
      totalCount: colorVariations.length,
      hasMore: colorVariations.length > swatches.length,
    };
  }

  /**
   * Generate complete product HTML
   * @param {object} data - All product data
   * @returns {string} HTML string
   */
  static generateProductHTML(data) {
    const { product, config, imageData, pricingData, attributes, badges, swatches } = data;

    return `
      <li class="product-item splide__slide" data-product-id="${attributes.id}">
        <div class="${config.cardStyle}">
          ${this.generateImageHTML(imageData, attributes, config)}
          ${this.generateContentHTML(attributes, pricingData, badges, swatches, config)}
        </div>
      </li>
    `;
  }

  /**
   * Generate srcset attribute for responsive images
   *
   * Pairs with the `sizes` attribute in generateImageHTML, which mirrors the
   * card widths in product-recommendations{,-grid}.liquid (50% mobile,
   * 33.333% from 576px, 25% from 992px). `sizes` previously claimed 90vw on
   * mobile for a 50vw card, so the browser resolved ~371px, applied DPR and
   * took the 800w candidate where ~400w covers the slot. Keep the two in sync:
   * if the grid breakpoints move, `sizes` has to move with them.
   *
   * @param {string} imageUrl - Base image URL
   * @returns {string} Srcset string
   */
  static generateImageSrcset(imageUrl) {
    if (!imageUrl) return "";

    const baseUrl = imageUrl.split("?")[0];

    const sizes = [200, 300, 400, 500, 600, 800];

    return sizes
      .map((size) => {
        const sizedUrl = ConstructorUtils.buildSizedImageUrl(baseUrl, size, size);
        return `${sizedUrl} ${size}w`;
      })
      .join(", ");
  }

  /**
   * Generate image HTML section
   * @param {object} imageData - Image data
   * @param {object} attributes - Product attributes
   * @param {object} config - Product configuration
   * @returns {string} Image HTML
   */
  static generateImageHTML(imageData, attributes, config) {
    // Generate srcset for responsive images
    const primarySrcset = this.generateImageSrcset(imageData.primarySrc);
    const secondarySrcset = imageData.secondarySrc ? this.generateImageSrcset(imageData.secondarySrc) : "";

    const secondaryImage = imageData.secondarySrc
      ? `<img 
          srcset="${secondarySrcset}"
          src="${imageData.secondarySrc}" 
          sizes="(min-width: 992px) 25vw, (min-width: 576px) 33vw, 50vw"
          alt="${imageData.alt}" 
          class="product-image-secondary" 
          loading="lazy"
          fetchpriority="low"
          decoding="async"
          width="${config.imageWidth}"
          height="${config.imageHeight}"
        />`
      : "";

    return `
      <div class="product-image-wrapper">
        <a href="${attributes.url}" class="product-link" tabindex="-1" aria-hidden="true"></a>
        <img 
          srcset="${primarySrcset}"
          src="${imageData.primarySrc}" 
          sizes="(min-width: 992px) 25vw, (min-width: 576px) 33vw, 50vw"
          alt="${imageData.alt}" 
          class="product-image"
          loading="${imageData.loading}"
          fetchpriority="${imageData.fetchpriority}"
          decoding="async"
          width="${config.imageWidth}"
          height="${config.imageHeight}"
        />
        ${secondaryImage}
        ${this.generateBadgeHTML(attributes.badges)}
        ${this.generateWishlistHTML(attributes.id, config)}
      </div>
    `;
  }

  /**
   * Generate content HTML section
   * @param {object} attributes - Product attributes
   * @param {object} pricingData - Pricing data
   * @param {object} badges - Badge data
   * @param {object} swatches - Swatches data
   * @param {object} config - Product configuration
   * @returns {string} Content HTML
   */
  static generateContentHTML(attributes, pricingData, badges, swatches, config) {
    const cardBodyClass = config.cardStyle === "card" ? "card-body" : "";

    return `
      <div class="firstline-container ${cardBodyClass}">
        <h3 class="product-item-title">
          <a href="${attributes.url}" class="product-link">${attributes.title}</a>
        </h3>
        ${this.generatePriceHTML(pricingData)}
        ${this.generateRatingHTML(config)}
        ${swatches ? this.generateSwatchesHTML(swatches) : ""}
      </div>
    `;
  }

  /**
   * Generate badge HTML
   * @param {object} badges - Badge data
   * @returns {string} Badge HTML
   */
  static generateBadgeHTML(badges) {
    if (!badges) return "";

    let badgeHTML = "";

    if (badges.primary) {
      badgeHTML += `<span class="img-badge ${badges.primary.class}">${badges.primary.text}</span>`;
    }

    if (badges.sale) {
      badgeHTML += `<span class="img-badge ${badges.sale.class}">${badges.sale.text}</span>`;
    }

    return badgeHTML;
  }

  /**
   * Generate price HTML
   * @param {object} pricingData - Pricing data
   * @returns {string} Price HTML
   */
  static generatePriceHTML(pricingData) {
    const compValueLabel = window.theme?.product?.compValue || "Comp. value";
    const priceDisplayStyle = window.theme?.product?.priceDisplayStyle || "comp_value";

    if (pricingData.isOnSale) {
      if (priceDisplayStyle === "strikethrough") {
        return `
          <div class="product-price">
            <span class="visually-hidden">Regular price, </span>
            <span class="product-price-compare">${pricingData.formattedComparePrice}</span>
            <span class="visually-hidden">Sale price, </span>
            <span class="product-price-final">${pricingData.formattedPrice}</span>
          </div>
        `;
      }
      return `
        <div class="product-price">
          <span class="visually-hidden">Sale price, </span>
          <span class="product-price-final">${pricingData.formattedPrice}</span>
          <span class="product-price-comp-value text-muted ms-2">${pricingData.formattedComparePrice} ${compValueLabel}</span>
        </div>
      `;
    }

    return `
      <div class="product-price">
        <span class="product-price-final">${pricingData.formattedPrice}</span>
      </div>
    `;
  }

  /**
   * Generate wishlist HTML
   * @param {string} productId - Product ID
   * @param {object} config - Product configuration
   * @returns {string} Wishlist HTML
   */
  static generateWishlistHTML(productId, config) {
    if (!config.showWishlist) return "";

    const translations = ConstructorUtils.getThemeConfig("wishlist.translations") || {};

    return `
      <button 
        class="btn-wishlist" 
        data-product-id="${productId}"
        aria-label="${translations.addToWishlist || "Add to wishlist"}"
        type="button"
      >
        <svg class="icon-heart" width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
        </svg>
      </button>
    `;
  }

  /**
   * Generate rating HTML
   * @param {object} config - Product configuration
   * @returns {string} Rating HTML
   */
  static generateRatingHTML(config) {
    if (!config.showRating) return "";

    return `
      <div class="product-rating">
        <div class="stars" role="img" aria-label="5 out of 5 stars">
          ${"★".repeat(5)}
        </div>
      </div>
    `;
  }

  /**
   * Generate swatches HTML
   * @param {object} swatches - Swatches data
   * @returns {string} Swatches HTML
   */
  static generateSwatchesHTML(swatches) {
    if (!swatches?.swatches?.length) return "";

    const swatchHTML = swatches.swatches
      .map(
        (swatch) => `
      <span 
        class="variant-swatch" 
        style="background-color: ${swatch.value.toLowerCase()}"
        title="${swatch.value}"
        data-variant-value="${swatch.value}"
      ></span>
    `
      )
      .join("");

    const moreText = swatches.hasMore
      ? `<span class="swatch-more">+${swatches.totalCount - swatches.swatches.length}</span>`
      : "";

    return `
      <div class="variant-swatches">
        ${swatchHTML}
        ${moreText}
      </div>
    `;
  }
}

if (typeof window !== "undefined") {
  window.ConstructorTemplates = ConstructorTemplates;
}
